import { ApplicationRef, inject, Injectable } from '@angular/core';
import { filter, switchMap, take, tap } from 'rxjs/operators';
import { CookieService, PlatformService, ScriptLoaderService, WindowService } from 'ngx-unificator/services';
import { UserService } from './user/user.service';
import { PTAG_KEY } from './stag-detector.service';
import { deferBrowserTask } from '../helpers/defer-browser-task';

@Injectable({ providedIn: 'root' })
export class HotjarService {
  private _scriptLoader: ScriptLoaderService = inject(ScriptLoaderService);
  private _user: UserService = inject(UserService);
  private _window = inject(WindowService);
  private _platform = inject(PlatformService);
  private _cookie = inject(CookieService);
  private _applicationRef = inject(ApplicationRef);

  private readonly HOTJAR_ID = 2698903;
  private readonly HOTJAR_VERSION = 6;

  private _nativeWindow: any = this._window.nativeWindow;

  /**
   * Init and load hot jar script
   */
  public initHotjar(): void {
    if (!this._platform.isBrowser) {
      return;
    }

    deferBrowserTask(this._applicationRef, () => this._initHotjar(), 2500);
  }

  private _initHotjar(): void {
    if (!this._platform.isBrowser) {
      return;
    }

    const w = this._nativeWindow;

    w.hj =
      w.hj ||
      function(...args: any[]) {
        (w.hj.q = w.hj.q || []).push(args);
      };

    w._hjSettings = {
      hjid: this.HOTJAR_ID,
      hjsv: this.HOTJAR_VERSION,
    };

    const url = `https://static.hotjar.com/c/hotjar-${this.HOTJAR_ID}.js?sv=${this.HOTJAR_VERSION}`;

    this._scriptLoader
      .load(url, 'head', { async: 'true' })
      .pipe(
        switchMap(() => this._user.auth$),
        filter((auth) => auth),
        take(1),
        tap(() => {
          const userInfo = this._user.info;

          this._identify(String(userInfo.id), {
            user_id: String(userInfo.id),
            s_tag: this._cookie.get(PTAG_KEY) ?? '',
          });
        }),
      )
      .subscribe();
  }

  /**
   * Send argument into hot jar service
   * @param userId
   * @param data
   * @private
   */
  private _identify(userId: string, data: Record<string, any> = {}): void {
    const w = this._nativeWindow;

    if (w.hj) {
      w.hj('identify', String(userId), {
        ...data,
      });
    }
  }
}
